import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { CheckCircle, Loader2, ArrowRight, Sparkles, Home } from 'lucide-react';
import confetti from 'canvas-confetti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionSuccess() {
  const { token, refreshCompanyContext } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState(null);
  
  const sessionId = searchParams.get('session_id');
  
  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setError('No session ID provided');
      setLoading(false);
    }
  }, [sessionId]);
  
  const checkPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;
    
    if (attempts >= maxAttempts) {
      setError('Payment verification timed out. Please check your email for confirmation.');
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/subscriptions/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.payment_status === 'paid') {
        setPaymentDetails(response.data);
        setLoading(false);
        
        // Trigger confetti celebration
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        
        // Refresh company context to update features
        if (refreshCompanyContext) {
          await refreshCompanyContext();
        }
        
        toast.success('Your subscription is now active!');
        return;
      } else if (response.data.status === 'expired') {
        setError('Payment session expired. Please try again.');
        setLoading(false);
        return;
      }
      
      // Continue polling
      setTimeout(() => checkPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setError('Failed to verify payment. Please contact support.');
      setLoading(false);
    }
  };
  
  const getPlanName = () => {
    const planId = paymentDetails?.plan_id;
    const plans = {
      'basic': 'Basic Memorial',
      'standard': 'Standard',
      'premium': 'Premium (with Worktops)'
    };
    return plans[planId] || planId;
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-8 pb-8 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-green-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Verifying Payment...</h2>
            <p className="text-slate-500">Please wait while we confirm your subscription.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="h-8 w-8 text-orange-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Verification Issue</h2>
            <p className="text-slate-500 mb-6">{error}</p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate('/subscription')}>
                Try Again
              </Button>
              <Button onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50 p-4">
      <Card className="w-full max-w-lg">
        <CardContent className="pt-8 pb-8 text-center">
          {/* Success Icon */}
          <div className="relative mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <div className="absolute -top-1 -right-1 left-0 right-0 flex justify-center">
              <Sparkles className="h-6 w-6 text-yellow-500 animate-pulse" />
            </div>
          </div>
          
          {/* Success Message */}
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Payment Successful!
          </h1>
          <p className="text-slate-600 mb-6">
            Your subscription has been activated. You now have access to all your plan features.
          </p>
          
          {/* Payment Details */}
          <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-medium text-slate-900 mb-3">Subscription Details</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Plan</span>
                <span className="font-medium text-slate-900">{getPlanName()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Amount</span>
                <span className="font-medium text-slate-900">
                  £{paymentDetails?.amount?.toFixed(2)}/month
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Status</span>
                <span className="font-medium text-green-600">Active</span>
              </div>
            </div>
          </div>
          
          {/* What's Next */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-medium text-blue-900 mb-2">What's Next?</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Your features have been automatically activated</li>
              <li>• A confirmation email has been sent to you</li>
              <li>• Your subscription renews monthly</li>
            </ul>
          </div>
          
          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              variant="outline" 
              onClick={() => navigate('/company-settings')}
              className="order-2 sm:order-1"
            >
              Company Settings
            </Button>
            <Button 
              onClick={() => navigate('/dashboard')}
              className="bg-green-600 hover:bg-green-700 order-1 sm:order-2"
            >
              <Home className="h-4 w-4 mr-2" />
              Go to Dashboard
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
